import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useGetVersions } from "@features/ix/erpIntegration/hooks";
import { Button } from "@shared/ui/button";
import { Eye } from "lucide-react";
import { VersionDetailDialog } from "@features/ix/shared/VersionDetailDialog";

export const VersionsTab: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [limit, setLimit] = useState(5);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any>(null);

  const { data: versionsData } = useGetVersions(id || "", { page: 1, limit });
  const versions = versionsData?.results || [];
  const totalCount = versionsData?.count || 0;

  return (
    <div className="p-2 space-y-6 animate-in fade-in duration-500">
      {/* Timeline Container */}
      <div className="relative pl-4 space-y-6">
        {/* Left vertical line */}
        <div className="absolute left-[25px] top-6 bottom-6 w-0.5 bg-gray-100" />

        {versions.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-10">
            No versions found.
          </div>
        )}

        {versions.map((ver: any) => (
          <div key={ver.id} className="flex gap-6 relative">
            {/* Timeline Circle Node */}
            <div className="flex items-start justify-center pt-5">
              {ver.isCurrent ? (
                <div className="w-4 h-4 rounded-full bg-[#5850ec] border-2 border-white shadow-sm ring-4 ring-indigo-50 z-10" />
              ) : (
                <div className="w-4 h-4 rounded-full border border-gray-250 bg-white shadow-sm z-10" />
              )}
            </div>

            <div className="flex-1 bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-2.5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-extrabold text-sm text-gray-900">
                    {ver.versionTag || "N/A"}
                  </span>
                  {ver.isCurrent && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                      Current
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedVersion(ver);
                    setIsViewDialogOpen(true);
                  }}
                  className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  <Eye className="w-4 h-4" /> View Details
                </button>
              </div>

              <p className="text-sm font-semibold text-gray-650 leading-relaxed">
                {ver.changeSummary || "N/A"}
              </p>

              <p className="text-xs text-gray-400 font-semibold">
                By {ver.createdUserRemoteName || "N/A"} ·{" "}
                {ver.createdDate || "N/A"}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      {versions.length > 0 && limit < totalCount && (
        <div className="flex justify-center mt-6 pt-4 border-t border-gray-100">
          <Button
            variant="outline"
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            onClick={() => setLimit((prev) => prev + 5)}
          >
            Load More Versions
          </Button>
        </div>
      )}

      <VersionDetailDialog
        isOpen={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        versionData={selectedVersion}
      />
    </div>
  );
};

export default VersionsTab;
